import React from "react";
import { connect } from "react-redux";
import { Button } from "../../Button";
import { Input, Select } from "../../Input";
import { Modal, DeleteModal } from "../../Modal";
import toastMessage from "../../../utils/toastMessage";
import centerQuery from "../../../utils/queries/bloodCenterQuery";
import centerSiteQuery from "../../../utils/queries/bloodCenterSiteQuery";
import fetchBloodCollectionPlan, {
  createBloodCollectionPlan,
  updateBloodCollectionPlan,
  deleteBloodCollectionPlan,
} from "../../../utils/queries/bloodCollectionPlanQuery";
import fetchBloodDonation from "../../../utils/queries/bloodDonationQuery";
import { defineRoleCenterSite, hasAllCenters } from "../../../utils/handleAccessRoles";
import { getStorage } from "../../../utils/storage";

const QUARTERS = [
  { label: "Q1 (Jan – Mar)", value: 1 },
  { label: "Q2 (Apr – Jun)", value: 2 },
  { label: "Q3 (Jul – Sep)", value: 3 },
  { label: "Q4 (Oct – Dec)", value: 4 },
];

const QUARTER_MONTHS = {
  1: [0, 1, 2],
  2: [3, 4, 5],
  3: [6, 7, 8],
  4: [9, 10, 11],
};

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => {
  const y = currentYear - 2 + i;
  return { label: String(y), value: y };
});

class AnnualPlan extends React.Component {
  state = {
    isAdmin: false,
    roleCenter: null,
    roleCenterSite: null,

    filterYear: currentYear,
    filterCenter: null,
    filterCenterSite: null,
    centers: [],
    centerSites: [],
    plans: [],
    isLoading: false,

    // modal
    showModal: false,
    showDeleteModal: false,
    editRecord: null,

    // form
    formCenter: null,
    formCenterSite: null,
    formCenterSites: [],
    formYear: currentYear,
    formQuarter: null,
    formPlannedSessions: "",
    formPlannedUnits: "",
    formError: {},
    isSaving: false,
    deleteId: null,
    isDeleting: false,

    // performed counts keyed by "siteId-quarter"
    performed: {},
  };

  async componentDidMount() {
    await this.loadRole();
    this.getCenters();
    this.fetchPlans();
  }

  loadRole = async () => {
    const { centerRole, centerSiteRole } = await defineRoleCenterSite();
    const user = await getStorage();
    const isSuperAdmin = user?.role === "superAdmin";
    const isCentral = hasAllCenters(user?.center);
    const isAdmin = isSuperAdmin || isCentral || (!centerRole && !centerSiteRole);
    this.setState({ isAdmin, roleCenter: centerRole, roleCenterSite: centerSiteRole });
  };

  getCenters = async () => {
    const data = await centerQuery(this.props.defaultLanguage, { type: "dropdown" });
    this.setState({ centers: data || [] });
  };

  getCenterSites = async (centerId) => {
    const data = await centerSiteQuery(this.props.defaultLanguage, {
      type: "dropdown",
      center: centerId,
    });
    this.setState({ centerSites: data || [] });
  };

  getFormCenterSites = async (centerId) => {
    const data = await centerSiteQuery(this.props.defaultLanguage, {
      type: "dropdown",
      center: centerId,
    });
    this.setState({ formCenterSites: data || [] });
  };

  fetchPlans = async () => {
    const { filterYear, filterCenter, filterCenterSite, isAdmin, roleCenter, roleCenterSite } = this.state;
    this.setState({ isLoading: true });
    try {
      const params = { year: filterYear, limit: 200, page: 1 };

      if (isAdmin) {
        if (filterCenter?.value) params.center = filterCenter.value;
        if (filterCenterSite?.value) params.centerSite = filterCenterSite.value;
      } else {
        if (roleCenterSite) params.centerSite = roleCenterSite?._id || roleCenterSite;
        else if (roleCenter) params.center = roleCenter?._id || roleCenter;
      }

      const res = await fetchBloodCollectionPlan(this.props.defaultLanguage, params);
      const plans = res?.data || [];
      this.setState({ plans, isLoading: false });
      this.fetchPerformed(plans, filterYear);
    } catch {
      this.setState({ isLoading: false });
    }
  };

  fetchPerformed = async (plans, year) => {
    if (!plans.length) return;
    const performed = {};
    const siteIds = [...new Set(plans.map((p) => p.centerSite?._id || p.centerSite))];

    await Promise.all(
      siteIds.map(async (siteId) => {
        for (const q of [1, 2, 3, 4]) {
          const months = QUARTER_MONTHS[q];
          const startDate = `${year}-${String(months[0] + 1).padStart(2, "0")}-01 00:00`;
          const lastMonth = months[2] + 1;
          const endDay = new Date(year, lastMonth, 0).getDate();
          const endDate = `${year}-${String(lastMonth).padStart(2, "0")}-${endDay} 23:59`;

          try {
            const res = await fetchBloodDonation(this.props.defaultLanguage, {
              centerSite: siteId,
              startDate,
              endDate,
              limit: 1,
              page: 1,
            });
            const count = res?.totalRecords || res?.count || 0;
            performed[`${siteId}-${q}`] = count;
          } catch {
            performed[`${siteId}-${q}`] = 0;
          }
        }
      })
    );

    this.setState({ performed });
  };

  openAddModal = () => {
    this.setState({
      showModal: true,
      editRecord: null,
      formCenter: null,
      formCenterSite: null,
      formCenterSites: [],
      formYear: this.state.filterYear,
      formQuarter: null,
      formPlannedSessions: "",
      formPlannedUnits: "",
      formError: {},
    });
  };

  openEditModal = (record) => {
    const quarter = QUARTERS.find((q) => q.value === record.quarter) || null;
    const formCenter = record.center
      ? { label: record.center.name || record.center._id, value: record.center._id || record.center }
      : null;
    const formCenterSite = record.centerSite
      ? { label: record.centerSite.name || record.centerSite._id, value: record.centerSite._id || record.centerSite }
      : null;

    this.setState({
      showModal: true,
      editRecord: record,
      formCenter,
      formCenterSite,
      formCenterSites: formCenterSite ? [formCenterSite] : [],
      formYear: record.year,
      formQuarter: quarter,
      formPlannedSessions: String(record.plannedSessions || ""),
      formPlannedUnits: String(record.plannedUnits || ""),
      formError: {},
    });

    if (formCenter?.value) {
      this.getFormCenterSites(formCenter.value);
    }
  };

  closeModal = () => this.setState({ showModal: false, editRecord: null });

  validateForm = () => {
    const { formCenter, formCenterSite, formYear, formQuarter, formPlannedSessions, formPlannedUnits } = this.state;
    const errors = {};
    if (!formCenter) errors.formCenter = "Required";
    if (!formCenterSite) errors.formCenterSite = "Required";
    if (!formYear) errors.formYear = "Required";
    if (!formQuarter) errors.formQuarter = "Required";
    if (!formPlannedSessions) errors.formPlannedSessions = "Required";
    if (!formPlannedUnits) errors.formPlannedUnits = "Required";
    this.setState({ formError: errors });
    return Object.keys(errors).length === 0;
  };

  saveRecord = async () => {
    if (!this.validateForm()) return;
    const { formCenter, formCenterSite, formYear, formQuarter, formPlannedSessions, formPlannedUnits, editRecord } = this.state;

    this.setState({ isSaving: true });
    try {
      const body = {
        center: formCenter.value,
        centerSite: formCenterSite.value,
        year: Number(formYear),
        quarter: formQuarter.value,
        plannedSessions: Number(formPlannedSessions),
        plannedUnits: Number(formPlannedUnits),
      };

      if (editRecord) {
        body.id = editRecord._id;
        await updateBloodCollectionPlan(this.props.defaultLanguage, body);
        toastMessage("success", "Plan updated");
      } else {
        await createBloodCollectionPlan(this.props.defaultLanguage, body);
        toastMessage("success", "Plan created");
      }

      this.closeModal();
      this.fetchPlans();
    } catch (err) {
      toastMessage("error", err?.response?.data?.error || "Save failed");
    }
    this.setState({ isSaving: false });
  };

  confirmDelete = (id) => this.setState({ showDeleteModal: true, deleteId: id });
  closeDeleteModal = () => this.setState({ showDeleteModal: false, deleteId: null });

  deleteRecord = async () => {
    this.setState({ isDeleting: true });
    try {
      await deleteBloodCollectionPlan(this.props.defaultLanguage, this.state.deleteId);
      toastMessage("success", "Plan deleted");
      this.closeDeleteModal();
      this.fetchPlans();
    } catch {
      toastMessage("error", "Delete failed");
    }
    this.setState({ isDeleting: false });
  };

  renderAnnualSummary() {
    const { plans, performed, filterYear } = this.state;
    if (!plans.length) return null;

    // Group by site then quarter
    const bysite = {};
    plans.forEach((p) => {
      const siteId = p.centerSite?._id || p.centerSite;
      const siteName = p.centerSite?.name || siteId;
      if (!bysite[siteId]) bysite[siteId] = { siteName, quarters: {} };
      bysite[siteId].quarters[p.quarter] = p;
    });

    const quarterTotals = { 1: { planned: 0, performed: 0, plannedUnits: 0 },
                            2: { planned: 0, performed: 0, plannedUnits: 0 },
                            3: { planned: 0, performed: 0, plannedUnits: 0 },
                            4: { planned: 0, performed: 0, plannedUnits: 0 } };

    return (
      <div style={{ overflowX: "auto" }}>
        <div className="report-title" style={{ textAlign: "center", fontWeight: "bold", fontSize: 16, marginBottom: 8 }}>
          ANNUAL BLOOD COLLECTION PLAN — {filterYear}
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#f0f0f0" }}>
              <th style={thStyle} rowSpan={2}>Center Site</th>
              {QUARTERS.map((q) => (
                <th key={q.value} style={thStyle} colSpan={2}>{q.label}</th>
              ))}
              <th style={thStyle} rowSpan={2}>Total Planned</th>
              <th style={thStyle} rowSpan={2}>Total Performed</th>
            </tr>
            <tr style={{ background: "#f0f0f0" }}>
              {QUARTERS.map((q) => (
                <React.Fragment key={q.value}>
                  <th style={{ ...thStyle, fontSize: 11 }}>Planned</th>
                  <th style={{ ...thStyle, fontSize: 11 }}>Performed</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(bysite).map(([siteId, { siteName, quarters }]) => {
              let totalPlanned = 0;
              let totalPerformed = 0;

              return (
                <tr key={siteId}>
                  <td style={tdStyle}>{siteName}</td>
                  {[1, 2, 3, 4].map((q) => {
                    const plan = quarters[q];
                    const plannedSessions = plan?.plannedSessions || 0;
                    const perf = performed[`${siteId}-${q}`] || 0;
                    totalPlanned += plannedSessions;
                    totalPerformed += perf;
                    quarterTotals[q].planned += plannedSessions;
                    quarterTotals[q].performed += perf;
                    quarterTotals[q].plannedUnits += plan?.plannedUnits || 0;
                    return (
                      <React.Fragment key={q}>
                        <td style={{ ...tdStyle, textAlign: "center" }}>{plannedSessions || "—"}</td>
                        <td style={{ ...tdStyle, textAlign: "center", color: perf >= plannedSessions && plannedSessions > 0 ? "#27ae60" : "#c0392b" }}>
                          {perf || 0}
                        </td>
                      </React.Fragment>
                    );
                  })}
                  <td style={{ ...tdStyle, textAlign: "center", fontWeight: "bold" }}>{totalPlanned}</td>
                  <td style={{ ...tdStyle, textAlign: "center", fontWeight: "bold" }}>{totalPerformed}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ background: "#f5f5f5", fontWeight: "bold" }}>
              <td style={tdStyle}>TOTAL</td>
              {[1, 2, 3, 4].map((q) => (
                <React.Fragment key={q}>
                  <td style={{ ...tdStyle, textAlign: "center" }}>{quarterTotals[q].planned}</td>
                  <td style={{ ...tdStyle, textAlign: "center" }}>{quarterTotals[q].performed}</td>
                </React.Fragment>
              ))}
              <td style={{ ...tdStyle, textAlign: "center" }}>
                {[1, 2, 3, 4].reduce((s, q) => s + quarterTotals[q].planned, 0)}
              </td>
              <td style={{ ...tdStyle, textAlign: "center" }}>
                {[1, 2, 3, 4].reduce((s, q) => s + quarterTotals[q].performed, 0)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  }

  renderPlanList() {
    const { plans, isLoading } = this.state;
    if (isLoading) return <div style={{ padding: 24, textAlign: "center" }}>Loading...</div>;
    if (!plans.length) return <div style={{ padding: 24, textAlign: "center", color: "#888" }}>No plans found. Click "+ Add Plan" to create one.</div>;

    return (
      <div style={{ overflowX: "auto", marginTop: 16 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#f0f0f0" }}>
              <th style={thStyle}>Center</th>
              <th style={thStyle}>Site</th>
              <th style={thStyle}>Year</th>
              <th style={thStyle}>Quarter</th>
              <th style={thStyle}>Planned Sessions</th>
              <th style={thStyle}>Planned Units</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {plans.map((p) => {
              const q = QUARTERS.find((q) => q.value === p.quarter);
              return (
                <tr key={p._id}>
                  <td style={tdStyle}>{p.center?.name || "—"}</td>
                  <td style={tdStyle}>{p.centerSite?.name || "—"}</td>
                  <td style={tdStyle}>{p.year}</td>
                  <td style={tdStyle}>{q?.label || p.quarter}</td>
                  <td style={{ ...tdStyle, textAlign: "center" }}>{p.plannedSessions}</td>
                  <td style={{ ...tdStyle, textAlign: "center" }}>{p.plannedUnits}</td>
                  <td style={{ ...tdStyle, textAlign: "center" }}>
                    <button
                      onClick={() => this.openEditModal(p)}
                      style={actionBtnStyle("#2980b9")}
                    >
                      Edit
                    </button>
                    {" "}
                    <button
                      onClick={() => this.confirmDelete(p._id)}
                      style={actionBtnStyle("#c0392b")}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  render() {
    const {
      filterYear, filterCenter, filterCenterSite, centers, centerSites,
      showModal, showDeleteModal, editRecord,
      formCenter, formCenterSite, formCenterSites, formYear, formQuarter,
      formPlannedSessions, formPlannedUnits, formError, isSaving, isDeleting, plans,
    } = this.state;

    return (
      <div>
        {/* Filters */}
        <div className="row" style={{ marginBottom: 16, alignItems: "flex-end" }}>
          <div className="col-md-2">
            <Select
              label="Year"
              options={YEARS}
              value={YEARS.find((y) => y.value === filterYear) || null}
              onChange={(v) => this.setState({ filterYear: v?.value || currentYear })}
            />
          </div>
          <div className="col-md-3">
            <Select
              label="Center"
              options={centers}
              value={filterCenter}
              onChange={(v) => {
                this.setState({ filterCenter: v, filterCenterSite: null, centerSites: [] });
                if (v?.value) this.getCenterSites(v.value);
              }}
              isClearable
            />
          </div>
          <div className="col-md-3">
            <Select
              label="Site"
              options={centerSites}
              value={filterCenterSite}
              onChange={(v) => this.setState({ filterCenterSite: v })}
              isDisabled={!filterCenter}
              isClearable
            />
          </div>
          <div className="col-md-2" style={{ display: "flex", gap: 8 }}>
            <Button text="Search" onPress={this.fetchPlans} />
            <Button text="+ Add Plan" onPress={this.openAddModal} className="btn-default" />
          </div>
          {plans.length > 0 && (
            <div className="col-md-2" style={{ display: "flex", alignItems: "flex-end" }}>
              <Button text="Print" onPress={() => window.print()} className="btn-default" />
            </div>
          )}
        </div>

        {/* Annual summary table */}
        <div id="printable-annual-plan">
          <style>{`
            @media print {
              body > *:not(#printable-annual-plan) { display: none; }
              #printable-annual-plan { display: block !important; }
            }
          `}</style>
          {this.renderAnnualSummary()}
        </div>

        {/* Detailed plan list */}
        <div style={{ marginTop: 24 }} className="no-print">
          <div style={{ fontWeight: "bold", fontSize: 14, marginBottom: 8, borderBottom: "1px solid #ddd", paddingBottom: 4 }}>
            Plan Records
          </div>
          {this.renderPlanList()}
        </div>

        {/* Add / Edit Modal */}
        <Modal
          show={showModal}
          handleClose={this.closeModal}
          title={editRecord ? "Edit Plan" : "Add Plan"}
          size="md"
        >
          <div className="modal-body">
            <div className="row">
              <div className="col-md-12">
                <Select
                  label="Year"
                  options={YEARS}
                  value={YEARS.find((y) => y.value === formYear) || null}
                  onChange={(v) => this.setState({ formYear: v?.value || currentYear })}
                  error={formError.formYear}
                />
              </div>
              <div className="col-md-12">
                <Select
                  label="Center"
                  options={centers}
                  value={formCenter}
                  onChange={(v) => {
                    this.setState({ formCenter: v, formCenterSite: null, formCenterSites: [] });
                    if (v?.value) this.getFormCenterSites(v.value);
                  }}
                  error={formError.formCenter}
                />
              </div>
              <div className="col-md-12">
                <Select
                  label="Center Site"
                  options={formCenterSites}
                  value={formCenterSite}
                  onChange={(v) => this.setState({ formCenterSite: v })}
                  isDisabled={!formCenter}
                  error={formError.formCenterSite}
                />
              </div>
              <div className="col-md-12">
                <Select
                  label="Quarter"
                  options={QUARTERS}
                  value={formQuarter}
                  onChange={(v) => this.setState({ formQuarter: v })}
                  error={formError.formQuarter}
                />
              </div>
              <div className="col-md-6">
                <Input
                  label="Planned Sessions"
                  type="number"
                  value={formPlannedSessions}
                  onChange={(e) => this.setState({ formPlannedSessions: e.target.value })}
                  error={formError.formPlannedSessions}
                />
              </div>
              <div className="col-md-6">
                <Input
                  label="Planned Units"
                  type="number"
                  value={formPlannedUnits}
                  onChange={(e) => this.setState({ formPlannedUnits: e.target.value })}
                  error={formError.formPlannedUnits}
                />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <Button text="Cancel" onPress={this.closeModal} className="btn-default" />
            <Button text={editRecord ? "Update" : "Save"} onPress={this.saveRecord} isSubmitting={isSaving} />
          </div>
        </Modal>

        {/* Delete Modal */}
        <Modal
          show={showDeleteModal}
          handleClose={this.closeDeleteModal}
          title="Delete Plan"
        >
          <DeleteModal
            handleCloseModal={this.closeDeleteModal}
            onDelete={this.deleteRecord}
            isDeleting={isDeleting}
          />
        </Modal>
      </div>
    );
  }
}

const thStyle = {
  border: "1px solid #ccc",
  padding: "6px 10px",
  textAlign: "left",
  fontWeight: "bold",
  fontSize: 12,
};

const tdStyle = {
  border: "1px solid #ddd",
  padding: "5px 10px",
};

const actionBtnStyle = (color) => ({
  background: color,
  color: "#fff",
  border: "none",
  borderRadius: 3,
  padding: "3px 10px",
  cursor: "pointer",
  fontSize: 12,
});

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;
  return { defaultLanguage };
};

export default connect(mapStateToProps)(AnnualPlan);
